
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { WorkoutHeader } from "./WorkoutHeader";
import { WorkoutSearch } from "./WorkoutSearch";
import { WorkoutTable } from "./WorkoutTable";
import { WorkoutDetails } from "./WorkoutDetails";
import { DeleteWorkoutDialog } from "./DeleteWorkoutDialog";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { useLanguagePath } from "@/utils/language";
import { useTranslations } from "@/hooks/useTranslations";
import { useAuth } from "@/components/AuthProvider";

export const WorkoutsList = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [workoutToDelete, setWorkoutToDelete] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { getPath } = useLanguagePath();
  const { t } = useTranslations("workouts");
  const { user } = useAuth();

  const { data: workouts, isLoading } = useQuery({
    // Include user ID in the query key to isolate data between users
    queryKey: ["workouts", user?.id],
    queryFn: async () => {
      if (!user?.id) {
        console.error("No authenticated user found");
        return [];
      }
      
      console.log("Fetching workouts for trainer:", user.id);
      const { data, error } = await supabase
        .from("workouts")
        .select(`
          *,
          target_student:students (
            first_name,
            last_name
          )
        `)
        .eq("trainer_id", user.id) // Ensure we only get workouts for this trainer
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching workouts:", error);
        return [];
      }

      console.log("Workouts fetched:", data);
      return data || [];
    },
    enabled: !!user?.id,
    staleTime: 60000 // 1 minute
  });

  const handleDelete = async (workoutId: string) => {
    try {
      if (!user?.id) {
        throw new Error("No authenticated user found");
      }
      
      // First verify this workout belongs to the current trainer
      const { data: workoutData, error: verifyError } = await supabase
        .from("workouts")
        .select("id")
        .eq("id", workoutId)
        .eq("trainer_id", user.id)
        .single();
        
      if (verifyError || !workoutData) {
        throw new Error("Workout not found or you don't have permission to delete");
      }
      
      const { error } = await supabase
        .from("workouts")
        .delete()
        .eq("id", workoutId);

      if (error) throw error;

      toast.success(t("notifications.deleted"));
      setWorkoutToDelete(null);
      queryClient.invalidateQueries({ queryKey: ["workouts"] });
    } catch (error: any) {
      console.error("Error deleting workout:", error);
      toast.error(error.message || t("notifications.errorDeleting"));
    }
  };

  const handleDuplicate = async (workout: any) => {
    try {
      if (!user?.id) {
        throw new Error("No authenticated user found");
      }
      
      const { id, created_at, updated_at, target_student, ...workoutData } = workout;
      
      const { data: newWorkout, error: workoutError } = await supabase
        .from("workouts")
        .insert({
          ...workoutData,
          trainer_id: user.id // Ensure the duplicated workout is created by the current trainer
        })
        .select()
        .single();

      if (workoutError) throw workoutError;

      const { data: exercises, error: exercisesError } = await supabase
        .from("exercises")
        .select("*")
        .eq("workout_id", id);

      if (exercisesError) throw exercisesError;

      if (exercises && exercises.length > 0) {
        const newExercises = exercises.map(({ id: exerciseId, workout_id, created_at, updated_at, ...exercise }) => ({
          ...exercise,
          workout_id: newWorkout.id
        }));

        const { error: newExercisesError } = await supabase
          .from("exercises")
          .insert(newExercises);

        if (newExercisesError) throw newExercisesError;
      }

      toast.success(t("notifications.duplicated"));
      queryClient.invalidateQueries({ queryKey: ["workouts"] });
    } catch (error: any) {
      console.error("Error duplicating workout:", error);
      toast.error(error.message || t("notifications.errorDuplicating"));
    }
  };

  const toggleStatus = async (workoutId: string, currentStatus: string) => {
    try {
      if (!user?.id) {
        throw new Error("No authenticated user found");
      }
      
      // First verify this workout belongs to the current trainer
      const { data: workoutData, error: verifyError } = await supabase
        .from("workouts")
        .select("id")
        .eq("id", workoutId)
        .eq("trainer_id", user.id)
        .single();
        
      if (verifyError || !workoutData) {
        throw new Error("Workout not found or you don't have permission to update");
      }
      
      console.log("Toggling status for workout:", workoutId, "Current status:", currentStatus);
      const newStatus = currentStatus === "active" ? "inactive" : "active";
      
      const { error } = await supabase
        .from("workouts")
        .update({ status: newStatus })
        .eq("id", workoutId);

      if (error) throw error;

      console.log("Status updated successfully to:", newStatus);
      queryClient.invalidateQueries({ queryKey: ["workouts"] });
      toast.success(
        newStatus === "active" ? t("status.activated") : t("status.deactivated")
      );
    } catch (error: any) {
      console.error("Error toggling workout status:", error);
      toast.error(error.message || t("status.error"));
    }
  };

  const filteredWorkouts = workouts?.filter(
    (workout) => {
      const searchLower = searchTerm.toLowerCase();
      const studentName = workout.target_student 
        ? `${workout.target_student.first_name} ${workout.target_student.last_name}`.toLowerCase() 
        : '';
      
      return workout.title.toLowerCase().includes(searchLower) || 
        workout.description?.toLowerCase().includes(searchLower) || 
        workout.type?.toLowerCase().includes(searchLower) ||
        studentName.includes(searchLower);
    }
  );

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="space-y-2">
        <h2 className="text-3xl font-bold tracking-tight gradient-text text-slate-950">{t("title")}</h2>
        <p className="text-muted-foreground">
          {t("subtitle")}
        </p>
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href={getPath("/dashboard")} className="text-flame hover:text-flame/80">Dashboard</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="font-medium">{t("title")}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="relative flex-1 w-full sm:max-w-xs">
          <WorkoutSearch searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
        </div>
        <WorkoutHeader />
      </div>
      
      {isLoading ? (
        <div className="text-center py-4 bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-xl shadow-sm border border-slate-100/40 dark:border-gray-700/40 p-8">
          <div className="animate-pulse flex flex-col items-center justify-center">
            <div className="h-12 w-12 bg-slate-200 dark:bg-gray-700 rounded-full mb-4"></div>
            <div className="h-4 bg-slate-200 dark:bg-gray-700 rounded w-1/3 mb-2"></div>
            <div className="h-4 bg-slate-200 dark:bg-gray-700 rounded w-1/4"></div>
          </div>
        </div>
      ) : (
        <div className="bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-xl shadow-sm border border-slate-100/40 dark:border-gray-700/40 overflow-hidden">
          <WorkoutTable
            workouts={filteredWorkouts || []}
            onToggleStatus={toggleStatus}
            onDuplicate={handleDuplicate}
            onDelete={(workoutId) => setWorkoutToDelete(workoutId)}
          />
        </div>
      )}

      <DeleteWorkoutDialog
        isOpen={!!workoutToDelete}
        onClose={() => setWorkoutToDelete(null)}
        onConfirm={() => workoutToDelete && handleDelete(workoutToDelete)}
      />
    </div>
  );
};
